import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@radix-ui/react-collapsible';
import { ArrowRight, ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

interface AtsEnhancementsData {
  heading: string;
  contents: Array<{ title: string, content: string }>;
}

interface AtsEnhancementsSectionProps {
  data: AtsEnhancementsData;
}

const AtsEnhancementsSection: React.FC<AtsEnhancementsSectionProps> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [atsEnhancementsExpanded, setAtsEnhancementsExpanded] = useState(false);

  if (!data || !data.contents || data.contents.length === 0) {
    return null; // Don't render anything if there's no data
  }

  const hasLongContent =
    data.contents.length > 5;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="mt-10 mb-6 bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden transition-all duration-300 ease-in-out"
    >
      <CollapsibleTrigger className="flex items-center justify-between w-full px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/50">
        <div className="flex items-center space-x-2">
          <h3 className="text-base font-semibold">Resume Enhancements</h3>
          <span
            className={`px-2 py-0.5 text-sm rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400`}
          >
            {data.contents.length} - AI suggestions
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200  ${isOpen ? 'rotate-180' : ''}`}
        />
      </CollapsibleTrigger>

      <CollapsibleContent>
      <div
                      className={`space-y-2 overflow-hidden transition-all duration-300 ease-in-out ${
                        atsEnhancementsExpanded
                          ? 'max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300 dark:scrollbar-track-slate-800 dark:scrollbar-thumb-slate-600 pr-2'
                          : 'max-h-[100px]'
                      }`}
                    >
          {data.contents.map((section, index) => (
            <div key={index} className="px-6 py-4">

              <h6 className="flex items-center space-x-1">
                <ArrowRight className="w-4 h-4 text-orange-600 dark:text-teal-400" />
                <span className="text-md font-semibold">{section.title}</span>
              </h6>
              <ul className="list-disc list-inside text-md text-orange-600 dark:text-teal-400 space-y-2">
                {section.content.split('\n').map((item, index) => (
                  <li key={index} className="mt-2">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      
        




        {/* Single toggle button for both sections */}
        {hasLongContent && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setAtsEnhancementsExpanded(!atsEnhancementsExpanded)}
              className="group flex items-center space-x-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:shadow-md transition-all duration-200"
            >
              <span className="text-sm">
                {atsEnhancementsExpanded ? 'Show Less' : 'Show All'}
              </span>
              <svg
                className={`w-4 h-4 transition-transform duration-300 ease-in-out ${atsEnhancementsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        )}



      </CollapsibleContent>
    </Collapsible >
  );
};

export default AtsEnhancementsSection;
